/** Jedes Training bekommt eine eigene, lesbare URL unter
 * `/training/events/$slug` (§ Nutzeranfrage „jedes Training eine separate
 * Seite … Trainingsgruppe und Datum … enthalten"). Der **eigentliche**
 * Schlüssel bleibt die UUID (`training_events.id`) — sie steckt unverändert
 * am Ende des Slugs und wird beim Öffnen allein zum Nachschlagen benutzt
 * (`GET /training/events/:id/card`); der lesbare Teil davor ist reine
 * Kosmetik, ähnlich einer GitHub-Gist- oder Stack-Overflow-URL. Vorteil
 * gegenüber einer "echten" sprechenden ID: keine Migration/RLS-Änderung
 * nötig, und ein einmal verschickter Link bleibt gültig, selbst wenn Datum
 * oder Trainingsgruppe des Termins später geändert werden — der lesbare
 * Teil zeigt dann zwar noch den Stand zum Zeitpunkt des Linkbaus, aber der
 * Link selbst bricht nicht. */

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Ersetzt deutsche Umlaute/ß vor dem generischen Zeichen-Strip, statt sie
 * ersatzlos wegzufallen zu lassen (sonst würde "Jugend U15 (Süd)" zu
 * "jugend-u15-sd" statt "jugend-u15-sued"). */
function slugifyPart(s: string): string {
  return s
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Nur die erste Trainingsgruppe fließt in den Slug ein (bei mehreren
 * Zielgruppen einer Einheit) — der Slug ist ohnehin nur Kosmetik, ein
 * zweiter/dritter Gruppenname würde ihn vor allem länger statt lesbarer
 * machen. Eine Einheit ganz ohne Gruppenziel ("offene" Session) bekommt den
 * Platzhalter "training". */
export function buildTrainingEventSlug(event: {
  id: string;
  eventDate: string;
  trainingGroupNames: string[];
}): string {
  const groupPart = event.trainingGroupNames[0] ? slugifyPart(event.trainingGroupNames[0]) : 'training';
  return `${groupPart}-${event.eventDate}-${event.id}`;
}

export function buildTrainingEventPath(event: { id: string; eventDate: string; trainingGroupNames: string[] }): string {
  return `/training/events/${buildTrainingEventSlug(event)}`;
}

/** Liest die UUID aus einem Slug heraus, unabhängig vom lesbaren Vorsatz —
 * `null`, wenn am Ende keine gültige UUID steht (z. B. ein von Hand
 * verstümmelter Link). */
export function extractTrainingEventId(slug: string): string | null {
  const match = slug.match(UUID_RE);
  return match ? match[0] : null;
}
